import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';

import ConfigContext from '../../../contexts/ConfigContext';

// Components
import Meta from '../../shared/Meta';
import CostEstimator from '../content/CostEstimator';
import CtaImage from '../content/CtaImage';
import LogoList from '../content/LogoList';
import FeaturedCuisines from '../FeaturedCuisines';
import CateringCta from '../content/CateringCta';
import HowItWorks from '../content/HowItWorks';
import Testimonials from '../content/Testimonials';
import PricingFaq from '../faqs/PricingFaq';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';

function CateringPrices() {
  const { vehicle_type, city_name, site_title } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();

  return (
    <div className='CateringPage'>
      <Meta
        title='Food Truck Catering Prices & Cost'
        type='website'
        canonical='/food-truck-catering/prices'
        image='https://static.seattlefoodtruck.com/photos/catering-header.webp'
        description={`How much does food ${vehicleType} catering cost? Get transparent pricing from $15–$35 per person. Compare free quotes from ${city_name}&#39;s best food ${vehicleType}s on ${site_title}.`}
        structuredData={{
          cateringService: {
            name: `Food ${vehicle_type} Catering Prices in ${city_name}`,
            description: `Transparent food ${vehicleType} catering pricing in ${city_name}. Compare quotes, understand costs, and book the best ${vehicleType} for your budget.`,
            url: '/food-truck-catering/prices',
            serviceType: 'Food Truck Catering Pricing',
          },
          breadcrumbs: {
            listItems: [
              { '@type': 'ListItem', position: 2, name: 'Catering', item: '/catering' },
              { '@type': 'ListItem', position: 3, name: 'Catering Prices', item: '/food-truck-catering/prices' }
            ]
          }
        }}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering-header.webp' noLazy isResponsive />
          <div className={styles.Cta_copy}>
            <h1>Food {vehicle_type} Catering Prices — What Does It Cost?</h1>
            <p>Wondering how much food {vehicleType} catering costs? You&#39;re not alone — it&#39;s one of the most common questions we hear. The good news: food {vehicleType} catering is one of the most affordable and flexible catering options available, typically ranging from $15 to $35 per person. {site_title} makes it easy to compare real quotes from {city_name}&#39;s top food {vehicleType}s so you can find the perfect fit for your event and budget.</p>
            <p>
              <Link to='/catering/register' className='Button Button--mobile-w100'>Get Free Catering Quotes</Link>
            </p>
          </div>
        </div>
      </div>

      <section className='Section'>
        <LogoList title='Trusted by leading companies' titleClassName='u-textCenter' className='u-mt1' />
      </section>

      <CostEstimator title={`Food ${vehicle_type} Catering Cost Estimator`} />

      <section className='Section--extraPadding'>
        <h2 className='u-textCenter u-mb3'>Understanding Food {vehicle_type} Catering Costs</h2>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering2.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>Per-Person Pricing Ranges</h3>
            <p>Food {vehicleType} catering in {city_name} typically costs between $15 and $35 per person. Budget-friendly options like tacos, hot dogs, and sliders often fall in the $15–$20 range, while mid-tier cuisines such as BBQ, pizza, and Asian fusion run $20–$28 per person. Premium menus featuring seafood, gourmet fusion, or multi-course meals may range from $28–$35+ per person. The final price depends on your menu selection, guest count, and event details.</p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={styles.Cta}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering3.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>Factors That Affect Catering Cost</h3>
            <p>Several factors influence your final food {vehicleType} catering price. Guest count is the biggest driver — larger events often qualify for volume discounts. Cuisine type matters too, with premium ingredients costing more. Event type (corporate vs. casual), location and travel distance, service duration, and any special requests like dietary accommodations or custom branding can also affect pricing. {site_title} helps you compare quotes so you understand exactly what you&#39;re paying for.</p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering4.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>What&#39;s Included in the Price</h3>
            <p>Most food {vehicleType} catering quotes include the food, on-site preparation by professional staff, service during your event, setup, and cleanup. Many vendors also provide plates, napkins, utensils, and condiments at no extra charge. Some {vehicleType}s offer add-ons like dessert service, beverages, or extended hours for an additional fee. When you request quotes through {site_title}, each vendor clearly outlines what&#39;s included so there are no surprises.</p>
          </div>
        </div>
      </section>

      <HowItWorks
        text1={`Tell us about your event — date, location, guest count, and cuisine preferences.`}
        text2={`Compare transparent quotes from available food ${vehicleType}s and book the best fit for your budget.`}
      />

      <Testimonials title="See What Our Customers Say About Pricing" />

      <section className='Section--extraPadding'>
        <FeaturedCuisines />
      </section>

      <section className='Section--extraPadding'>
        <PricingFaq />
      </section>

      <section className='Section--extraPadding u-pt2'>
        <CateringCta title={`Get Free Food ${vehicle_type} Catering Quotes`} />
      </section>
    </div>
  );
}

export default CateringPrices;
